// Room registry — the three demo interiors (Demo Build Plan).
// Each room declares its splat manifest, a *designed spawn* (Demo DoD #2:
// aligned, eye-height, best angle — these are the defaults; a manifest may
// ship a `spawn` of its own which overrides these once the splat loads),
// and interior orbit clamps sized to the room so the camera can't leave it.
import { faceMarks } from './city/facing';
import HQ_GREETINGS from './data/hq_greetings.json';
export const ROOMS = {
  // "Your Room Is The Studio" — a USER-SCANNED room (phone RoomScan → Gemini
  // restyle → Marble bake; see API scripts/room_studio_spike.js). No city
  // anchor yet: enter directly with ?enter=room-studio. The manifest+spz land
  // in public/worlds/ from the bake's --download --out.
  'room-studio': {
    name: 'My Studio',
    manifest: '/worlds/room_studio.json',
    accent: '#ffb25e',
    // Seated-capture eye height; the pano was shot at the desk, so spawn
    // matches the capture point looking toward the console wall.
    spawn: { pos: [0, 1.35, 0.4], look: [0, 1.3, -1.2] },
    orbit: { min: 0.01, max: 2000, minPolar: 0, maxPolar: 3.14 },
    // MEET panel — dormant until a live meet lands (MeetDock → liveVideoEl):
    // no recorded cameo, so nothing renders until the artist goes live on it
    // (?panel=edit shows an amber ghost for placement). FIXED angle, not
    // billboarded: this panel is placed AS A SCREEN in the room — billboarding
    // is reserved for free-standing composited characters at open positions.
    // Tune with ?panel=edit (arrows move, u/j raise/lower, [ ] rotate,
    // -/= resize, b toggles billboard) and paste the logged line over this one.
    videoPanel: { pos: [0, 1.25, -1.3], rotY: 0, height: 1.4, billboard: false },
  },
  studio: {
    name: 'The Studio',
    manifest: '/worlds/studio.json',
    accent: '#ffb25e',
    // THE DOORMAN (Khari, 2026-08-03: "we have a 'bouncer' at the concert
    // hall.. use an instance of him and do the same thing for the studio").
    // Same split as `requires`: the RULE lives here, the CHECK lives in
    // enterInterior, the VOICE lives in AccessRefusal. This one is about the
    // STORY rather than the membership ladder — you get in when Spor1 has
    // driven you and the salon has made you presentable, in that order.
    requiresStory: true,
    // Entry view (user-framed): standing at the console looking dead-on at
    // the workstation + booth-window cameo. KEEP IN SYNC with the manifest's
    // spawn (the manifest wins once the splat loads).
    spawn: { pos: [0, 1.61, 1.84], look: [0, 1.6, 1.53] },
    // User-tuned in ?cam=edit: azimuth + polar fully open (free look), distance
    // effectively unclamped; bounds clamp still does wall containment.
    orbit: { min: 0.01, max: 2000, minPolar: 0, maxPolar: 3.14 },
    // Video cameo placement: couch side of the room, angled toward spawn.
    // Don't trust guesses here — tune live with ?panel=edit (arrows move,
    // PgUp/PgDn raise, [ ] rotate, -/= resize, Shift = fine) and paste the
    // console-logged line over this one.
    // Staged BEHIND the mic-booth window (the frame mattes the panel edges
    // and the frame/panel parallax feigns booth depth). billboard OFF — a
    // room behind glass must not swivel with the viewer; works with limited
    // orbit so users can't flank it. Paste tuned numbers from ?panel=edit.
    // Derived from the "on point" screenshot: card centered right-of-middle
    // behind the booth window plane (back wall ≈ z −2.4), mid-window height,
    // tall enough that the frame crops it. Fine-tune with ?panel=edit.
    videoPanel: { pos: [0.19, 0.57, -3.13], rotY: 0.08, height: 7.44, billboard: false },
    // THE WALL OF GOLD RECORDS. Each entry is one framed plaque on the studio
    // wall, and each holds ONE of the artist's own tracks — slot 0 is their
    // newest. Clicking one is where artwork and release live, so "put my record
    // out" gets a place in the world instead of a button on a website.
    //
    // Placed like `videoPanel` above, and for the same reason: this room is a
    // Gaussian SPLAT, so there is no mesh to attach a surface to and nothing for
    // the promotions tool's ghost to raycast onto. Hand-tuned transforms are the
    // only way to hang anything on a splat wall.
    //
    // Tuned in-world against the splat (⚙ → Record wall). The frames sit on the
    // left-hand wall, fanning slightly as it angles away — hence the drifting
    // rotY rather than one shared angle. Re-tune and paste over this block.
    plaques: [
      { pos: [-2.04, 1.65, 2.14], rotY: 2, size: 0.42 },
      { pos: [-2.36, 1.65, 1.54], rotY: 1.92, size: 0.42 },
      { pos: [-2.49, 1.65, 0.9], rotY: 1.76, size: 0.42 },
      { pos: [-2.6, 1.65, 0.27], rotY: 1.68, size: 0.42 },
    ],
    // RESULT CARDS — where a finished track lands, in front of you, the moment
    // the room hands it back. Two of them because a generation run gives you
    // takes to choose between, not one answer.
    //
    // The wall behind you is what you PUT OUT; these are what just came out of
    // the room, released or not. That split is the whole point: the reveal is
    // immediate and in your eyeline, and only a RELEASED record goes on the wall.
    //
    // Tuned in-world (⚙ → Records → Results). Angled in toward the chair, so
    // both takes face whoever is sitting at the board.
    resultCards: [
      { pos: [-0.57, 1.5, 0.31], rotY: 0.25, size: 0.55 },
      { pos: [0.52, 1.5, 0.34], rotY: -0.25, size: 0.55 },
    ],
    // Live Anam video chat — same brain/billing as the web Studio page.
    // When a session goes live, the booth-window card swaps its recorded
    // cameo for the live WebRTC stream (LiveChatDock → worldStore.liveVideoEl).
    liveChat: { avatarId: 26, name: 'Rebel Rhythm' }
  },
  'listening-room': {
    name: 'The Listening Room',
    manifest: '/worlds/listening-room.json',
    accent: '#ff5e78',
    spawn: { pos: [0, 1.6, 2.8], look: [0, 1.2, -1.4] },
    // pan: right-drag / two-finger slides the pivot so you can look anywhere,
    // including behind you (bounds still keep you off the fuzzy outer shell).
    orbit: { min: 0.5, max: 9, pan: true }
  },
  'concert-hall': {
    name: 'The Concert Hall',
    // NO `manifest`: the hall renders from its GLB (concert_hall_{desktop,
    // mobile}.glb, Interior.jsx ConcertHall). The old '/worlds/concert-hall.json'
    // never existed, so every visit fetched a 404 and logged an error for a
    // splat that could not load (SESSION_HANDOFF_2026-08-23 §4).
    accent: '#7dd3ff',
    // WHO GETS IN. A rung on the access ladder (services/access.js): 'everyone',
    // 'member', 'plan', 'body'. Absent means anyone, which is what every other
    // room in here is and should stay.
    //
    // On the ROOM rather than on the door, because there are six ways to open a
    // venue and only one of them has a guard standing next to it. The rule
    // belongs to the place; the guard is how the refusal is PERFORMED, and if
    // he is ever moved, deleted or not yet loaded the hall is still shut.
    //
    // Client-side, so this decides whether the door opens and what he says —
    // not whether the API will serve what is inside. See access.js.
    requires: 'plan',
    // firstPerson: free drag-look + WASD walk through the hall, kept inside by
    // the room bounds (and off the stage for non-admins) via CameraBounds.
    firstPerson: true,
    // In the crowd on the field, eye height, facing centre stage. Inside the
    // walk frontier (Interior.jsx HALL_BOX.fieldZMax = 6.2) — a spawn past it
    // gets snapped by CameraBounds on the first frame, which reads as a jolt.
    spawn: { pos: [0, 1.7, 5], look: [0, 2.2, -1.5] },
    orbit: { min: 0.5, max: 20, pan: true },
    // Interim shipped performer — a transparent-PNG cutout composited on the
    // stage. ?perfimg=<url> overrides it live; PerformerPanel tunes height/feet.
    // FUTURE: DB-driven via a `performances` table so artists can reserve + set
    // this stage slot dynamically. See reference_world_placeable_naming.
    performer: { img: '/performers/avva.png' },
  },
  theater: {
    name: 'The Cinema',
    accent: '#c8a2ff',
    // No splat — a built black-box theater: a big flat 16:9 IMAX screen on the
    // back wall playing the /listen music videos. firstPerson: drag-look + WASD,
    // kept in the room (and out of the screen wall) by CameraBounds.
    firstPerson: true,
    // Mid-house, eye height, facing the screen (screen sits on the back wall,
    // −z). A little back from center so the big screen fills the view.
    spawn: { pos: [0, 1.7, 9], look: [0, 6, -15] },
    orbit: { min: 0.5, max: 24, pan: true },
  },
  'record-store': {
    name: 'Wax & Vinyl',
    manifest: '/worlds/record-store.json',
    accent: '#9dff7d',
    // Blend: Marble splat shop + live overlay (RecordStore.jsx).
    // firstPerson: stand still and DRAG to look (rotate in place, no orbit
    // float); WASD to pan around the shop. rooms.js spawn is authoritative
    // (RecordStore does not push the manifest spawn) so ?cam=edit "copy spawn"
    // can re-aim the entry framing.
    spawn: { pos: [-0.29, 1.6, 1.53], look: [0.62, 1.55, -0.25] }, // ?cam=edit copy-spawn
    firstPerson: true,
    // LOOK ONLY (2026-11-02). Khari: "walking into the record shop in 3rd person
    // POV is broken and exposes the architecture. automatically place the view
    // into 1st person and disable walking, allowing only camera pivot." A splat
    // is a photograph you stand inside: step off the spot it was captured from,
    // or put the camera behind a body, and its seams and voids show. So here you
    // are always first person, at the spawn, and drag (or the arrow keys) only
    // turns the view — no WASD, no joystick, no third-person toggle
    // (PlanetScene InteriorWalk, Hud, GameHud).
    lookOnly: true,
    // CARDS — the featured sleeves hung on the splat wall. THIS ARRAY IS THE
    // SHELF SIZE: the track list is sliced to its length, so three entries show
    // three records however many the API returns. Lives here with the studio's
    // `plaques` and `resultCards` because a room's placeables belong to the room,
    // and because the ⚙ editor panel can then read them without importing the
    // 3D module. Tuned in-world (⚙ → Record sleeves).
    cards: [
      { pos: [1.55, 1.91, -1.99], rotY: -0.48, size: 0.5 },  // tuned
      { pos: [0.59, 1.91, -2.44], rotY: 0, size: 0.5 },      // tuned
      { pos: [2.38, 1.91, -1.5], rotY: -0.72, size: 0.5 },   // tuned
      { pos: [-0.37, 1.91, -1.99], rotY: 0.48, size: 0.5 },  // extrapolated
      { pos: [-1.2, 1.91, -1.5], rotY: 0.72, size: 0.5 },    // extrapolated
      { pos: [1.55, 1.29, -1.99], rotY: -0.48, size: 0.5 },  // extrapolated
      { pos: [0.59, 1.29, -2.44], rotY: 0, size: 0.5 },      // extrapolated
      { pos: [2.38, 1.29, -1.5], rotY: -0.72, size: 0.5 },   // extrapolated
    ],
    // RESIDENTS — avatars standing inside the splat. A splat has no geometry to
    // raycast, so there is nothing to drop a body onto and no floor to snap to:
    // every position here is authored by eye against the photoreal shop
    // (⚙ → Interior avatar). `slug` joins to models/avatars/roster.json.
    residents: [],
    // Tilt (pitch) limits for the first-person look — how far up/down the
    // visitor can look (radians; tune in ?cam=edit → "copy look config").
    look: { tiltMin: -0.12, tiltMax: 0.6 }, // ?cam=edit copy-look
    orbit: { min: 0.4, max: 12, pan: true }
  },

  // ── MESH VENUES ─────────────────────────────────────────────────────────
  // A room with a `model` is a GLB interior rather than a Gaussian splat, and is
  // rendered by the generic MeshInterior — no bespoke component. It loads when
  // entered and frees its GPU memory when left, which is what lets us keep
  // adding them.
  //
  //   model      the GLB (see scripts/extract_model.mjs for how these are built)
  //   fitTo      OPTIONAL longest-horizontal-span in world units. Omit when the
  //              model is already authored in metres, as both of these are.
  //   modelRotY  OPTIONAL yaw, for a model whose front faces the wrong way.
  //
  // The spawn and orbit values below are STARTING POINTS, placed at the model's
  // measured centre at eye height. Walk in with ?cam=edit and copy the tuned
  // spawn over them — every other room in this file was set that way.
  warehouse: {
    name: 'The Warehouse',
    accent: '#7dffd0',
    // The small club first tried as the concert hall (2026-08-18) and kept on
    // its own merits: "Listen Clean Live - 2 years" (Sketchfab, CC-BY-4.0 —
    // credit owed, see DOCS/WORLD/models/concert_stage/ATTRIBUTION.md).
    // Optimized 3,301 meshes → 78 draw calls, KTX2; `warehouse_mobile.glb`
    // sits beside it (1024px, 120k tris) for a per-device split if this one
    // proves heavy on phones — nothing reads it today.
    model: '/models/venues/warehouse.glb',
    // ── THE ROOM, measured (model units are METRES, 1:1) ─────────────────────
    // 7.1 wide × 20.1 long × 6.0 tall. DJ booth (CDJs at tabletop 0.9m) and
    // truss/speaker rig at the −z end facing +z; open concrete floor to the
    // corrugated wall at z≈6.6; mezzanine bar along +x at y≈3; a back room
    // beyond the wall. Booth centre sits at model (29.55, −2.1) — the venue is
    // authored ~31 off-origin on x; MeshInterior centres the footprint.
    //
    // ⚠ FALLBACK ONLY — a saved room_config row OUTRANKS this number, and the
    // live truth for this room is IN THE ROW (tuned in-world, the intended
    // workflow). The room's final size is a PRODUCT:
    //   fitTo (?venuefit → saved row → this) × saved placement scale × fit tune
    // which is how "the same fitTo" can render different sizes on different
    // days: a save redistributes the factors while the approved LOOK stays
    // constant (Khari's eye kept landing at the same effective size across
    // 18×1 and 10×~1.8 compositions, 2026-08-18). Tune ONE knob, save in ITS
    // panel, and read the [venue] console line — it prints which source won
    // and the placement multiplier.
    //
    // Khari's final eye-approved number (2026-08-18, after the compounding was
    // understood): ?venuefit=11 — which lands almost exactly on the CAST-TRUE
    // scale (~11 is the room proportioned to the resident cast). The earlier
    // "grand asymmetry" readings were the multiplier stack, not the eye.
    fitTo: 11,
    // Mid-floor facing the booth (fit ×0.548 → booth at ≈(−0.77, −2.36)).
    // pos[1] is the room's EYE HEIGHT and matches the street's 0.75 — the
    // fly-in used to land you at 1.3 and FloorClamp then sank you to 0.75 on
    // settle; spawning at the height the clamp will hold kills that entry dip.
    spawn: { pos: [0, 0.75, 2.0], look: [-0.77, 0.55, -2.36] },
    // Tonight's sets, on a board by the way in (VenueSetList; the Blackroom
    // inherits it). Room space at this fit; open floor there (raycast 2026-11-01).
    setList: { pos: [0.9, 0, 1.3], rotY: -0.91 },
    // NO `walk` BLOCK — mesh rooms bound by FloorClamp's raycast (see subway).
    firstPerson: true,
    // Max tracks the room: at fitTo 20.7 a 12-unit tether couldn't pull back
    // far enough to read the space.
    orbit: { min: 0.4, max: 22, pan: true },
    // WHOSE ROOM THIS IS (2026-10-31, API city_rooms / RoomHosts): a rock club
    // that belongs to the artist who hosts the rock station; its music is that
    // station (RoomSoundtrack, instead of the outside radio). The owner is drawn
    // here only while the city has them in (RoomHost), at the booth — the point
    // the underground-club type measured in this model at this fit (model x
    // 29.5, z −2.0: behind the CDJs, facing the room). When they are not in, a
    // marker-on-cardboard sign leans against the front of the decks with the
    // time they will next be in, and nothing else (RoomHostSign): the decks'
    // front is model z −1.35 → room z −1.949 (tabletop 0.9m → 0.49u), so the
    // sign stands on the floor just in front with its top against them, in the
    // spawn's eyeline. Instances of the type set their own `booth` and drop
    // `hostSign` (venueTypes instanceRoom).
    booth: { pos: [-0.7935, 0, -2.305], rotY: 0 },
    hostSign: { pos: [-0.7935, 0, -1.86], rotY: 0, tilt: 0.28 },
    // WALK OUT TO LEAVE (2026-10-31, Khari: "Add: the blackroom/warehouse"). A
    // downloaded model with no door meshes, so found by raycasting it at the
    // fit: the dance floor ends at a corrugated wall (room z 2.4) with a wide
    // opening (x ≈ −0.2..1.7) into a narrower back room (to z 5.5) — the way you
    // came in: the spawn stands just inside it facing the booth. Stepping back
    // through it leaves. The Blackroom (this model, venueTypes) inherits it.
    exit: { pos: [0.75, 0, 2.57], size: [1.6, 1.6, 0.3] },
  },
  // THE GANGSTAFUEL GYM (2026-10-31). Khari: "the 'GangstaFuel' Gym … black and
  // grey with neon lime green as the highlight. This will add the position of
  // 'fitness instructor' … a drink is available. It is 'GangstaFuel' Energy
  // Drink … they can use the treadmill (run in place), and a bench press (lay and
  // view upward)." Built by scripts/make_gym.mjs (room units = metres ÷ 2.2,
  // floor at 0, bbox symmetric: these ARE room space; scripts/out/gym_marks.json).
  // 12m × 18.5m, door at +z; three treadmills facing the mirror wall (west),
  // two bench presses (head toward the back wall), free weights, the fuel bar by
  // the door (east) with the fridge behind it. Stands in the city as the 'gym'
  // venue type, instance "GangstaFuel Gym" → room id 'gangstafuel-gym'.
  //   GymFloor reads the machines off the model (`use_treadmill_N`,
  //   `use_bench_N`, `bench_N_bar`, `treadmill_N_belt`): no list here to drift.
  gym: {
    name: 'GangstaFuel Gym',
    accent: '#74f84b',
    model: '/models/venues/gym.glb',
    // THE NEON OVER THE FRONT DOOR (2026-11-03, Khari: "a glowing neon version of
    // that logo above the front door. it should glow in the evening … slightly
    // 3D (not flat) so it appears like an actual sign"). The same logo as the
    // decals inside; components/NeonSign, placed by VenueFacades in place of the
    // lettered name board. `width` (world units) optional: else ~0.7 of the front.
    neonSign: { url: '/images/gangstafuel_logo.png', color: '#74f84b' },
    // Inside the door, looking down the gym.
    spawn: { pos: [-0.4545, 0.75, 3.5], look: [0, 0.7, -2.7273] },
    firstPerson: true,
    // tiltMax 1.5: lying on a bench you look straight up at the bar (GymFloor
    // LIE_PITCH 1.42), and the walk would otherwise clamp you back down.
    look: { tiltMin: -0.6, tiltMax: 1.5 },
    orbit: { min: 0.4, max: 30, pan: true },
    // The machines are shared, like the café's chairs: one person on a
    // treadmill, two minutes at a time (sockets/sharedSeatStore).
    sharedSeats: true,
    // WALK OUT THE DOOR TO LEAVE (2026-10-31, as HQ): the double glass doors in
    // the south wall (make_gym: x −1.0m, 1.8m wide, inner face z 9m = 4.09u) —
    // clear of the spawn (z 3.5).
    exit: { pos: [-0.4545, 0, 4.11], size: [0.82, 1.6, 0.24] },
    // THE FUEL BAR: where the instructor's can is set down (state/gymFuel).
    fuel: { pickup: [1.6136, 0.4773, 2.9545] },
    // THE COUNTER'S CHAT IS A SCENE (2026-11-01, Khari: "the gym instructor needs
    // the chat scene treatment added"; components/SceneChat). Per room on purpose:
    // the record shop's clerk shows his picks on the wall behind him and the print
    // shop opens its studio beside the dock — a close-up would hide both.
    chatScene: true,
    // Behind the bar, facing the room ('*' = whoever is cast as the instructor).
    stage: { '*': { bar: { pos: [2.1364, 0, 2.9545], rotY: -1.5708 } } },
    // The logo behind the bar (east wall) and big on the back wall (RoomDecals).
    decals: [
      { url: '/images/gangstafuel_logo.png', pos: [2.7282, 1.3636, 2.9545], size: [1.0909, 0.6136], rotY: -1.5708, glow: 0.7 },
      { url: '/images/gangstafuel_logo.png', pos: [0, 1.25, -4.0918], size: [1.8182, 1.0227], rotY: 0, glow: 0.7 },
    ],
  },
  // HYPETUNES HEADQUARTERS (2026-10-31). Khari: "HypeTunes Main Headquarters (a
  // helpdesk). Place Lyra at the front desk." Built by scripts/make_hq.mjs (room
  // units = metres ÷ 2.2, floor at 0, bbox symmetric so these ARE room space;
  // the numbers below are its printout, scripts/out/hq_marks.json). A lobby with
  // a glass front at +z: the desk faces the door, a free-standing logo wall
  // behind it (walkways either side into a lift lobby), a waiting area, two wall
  // screens (`postable`, for media later). Stands in the city as the
  // 'headquarters' venue type (venueTypes), placed behind the main spawn point.
  hq: {
    name: 'HypeTunes Headquarters',
    accent: '#ffb25e',
    model: '/models/venues/hq.glb',
    // Standing in the city (the 'headquarters' type is sited), the glass front
    // shows the real arrival street, so the painted street behind it comes down.
    sitedHide: ['street_view'],
    // Just inside the doors, looking at the desk.
    spawn: { pos: [0, 0.75, 2.682], look: [0, 0.705, 0.023] },
    firstPerson: true,
    look: { tiltMin: -0.5, tiltMax: 0.6 },
    orbit: { min: 0.4, max: 30, pan: true },
    // Behind the desk, facing the door: the platform's assistant (FrontDesk).
    frontDesk: {
      pos: [0, 0, 0.023], rotY: 0, who: 'assistant',
      // PRESET QUESTIONS (2026-11-01, Khari: "Place preset questions in Lyra's
      // chat in HQ. Perhaps chips."): offered as chips in her chat until you ask
      // your own (AvatarChatDock / GuestChatDock). Questions only — the answers
      // are hers, from the platform's knowledge.
      questions: [
        'What is HypeTunes?',
        'How do I make my own artist?',
        'Can my artist live in HypeTown?',
        'What can I do here in HypeTown?',
        "What's free, and what do the plans include?",
      ],
      // HER CHAT IS A SCENE (2026-11-01, components/SceneChat): the camera comes
      // to her, up close and facing you, and the chat lays itself over the shot.
      scene: true,
      // THE CLIPBOARD (2026-11-01, Khari: "there is nothing in the UI to tell me
      // to click on Lyra to start a conversation. Please add a button on a
      // standing clipboard on the desk."): on the ledge just left of her (the
      // visitor's left; the check-in tablet has the right end), turned to the door.
      // Room space: 0.62 m left of centre, the ledge's middle at 1.1 m (make_hq).
      clipboard: { pos: [-0.282, 0.5, 0.596], rotY: 0.134 },
    },
    // HER RECORDED GREETING when you walk in (2026-11-01, components/RoomGreeting):
    // one unhurried shot toward the desk, one of her own lines in her voice
    // (data/hq_greetings.json, recorded by API scripts/record_room_greetings.js).
    greeting: HQ_GREETINGS,
    // WALK OUT THE DOOR TO LEAVE (2026-10-31, Khari: "there is a huge door in
    // front. walking out of it should trigger the exit"): the 2.4m open doorway
    // in the glass front (make_hq: ±1.2m, inner face z 7m = 3.18u, the floor
    // runs 0.25m past it), from just inside the face to the floor's edge — clear
    // of the spawn on the mat (z 2.68). Portals turns it into a way out; you land
    // in front of HQ's door, facing it.
    exit: { pos: [0, 0, 3.2], size: [1.09, 1.6, 0.24] },
    // The HypeTunes logo on the face of the logo wall (RoomDecals; 1000×351 on
    // transparency, fitted inside the 2:1 area the builder left clear).
    decals: [{ url: '/logo.png', pos: [0, 1.227, -0.74], size: [1.455, 0.511], rotY: 0, glow: 0.35 }],
  },
  // THE HYPEFLYER PRINT SHOP (2026-10-31). Khari: "We are going to change the
  // laudromat into a flyer and graphics shop. Users will go there and be able to
  // make images, posts, whois cards and presskits … a front desk with a clerk
  // and next to them can be the options on a panel/modal as they walk you
  // through them." Built by scripts/make_printshop.mjs (room units = metres ÷
  // 2.2, bbox symmetric: these ARE room space, from scripts/out/
  // printshop_marks.json). Sized to the laundromat's body (5.15 × 4.6u) and
  // stands IN it: the 'print-shop' venue type is sited on `LM_Laundrette`
  // (migration 462), so the street shows through its windows and the old
  // laundromat steps aside while you are inside.
  //   THE CLERK is whoever is cast in the job (migration 463, ⚙ → Interior
  //   avatar → "Clerk"), standing at `counter`. Their dock is the shop's
  //   ordinary counter chat (ShopDock), and `printshop` opens THE STUDIO beside
  //   it (components/printshop/PrintStudio): images, posts, Who Is cards, press
  //   kits, on the website's own endpoints and prices.
  printshop: {
    name: 'HypeFlyer Print Shop',
    accent: '#ff4fa3',
    model: '/models/venues/printshop.glb',
    // Just inside the door, looking at the clerk over the counter.
    spawn: { pos: [0.818, 0.75, 1.682], look: [-0.545, 0.705, -1.886] },
    firstPerson: true,
    look: { tiltMin: -0.5, tiltMax: 0.6 },
    orbit: { min: 0.4, max: 30, pan: true },
    // WALK OUT THE DOOR TO LEAVE: the laundromat's doorway (1.8m, right of
    // centre in the storefront), from just inside the glass to its outer face.
    exit: { pos: [0.818, 0, 2.25], size: [0.736, 1.6, 0.24] },
    // Behind the counter, facing the door ('*' = whoever is cast as the clerk).
    stage: { '*': { counter: { pos: [-0.545, 0, -1.886], rotY: 0 } } },
    // The studio panel opens with the clerk's dock (ShopDock → PrintStudio), and
    // from the studio screen at the clerk's elbow (PrintShopScreen: this box is
    // its face, from make_printshop: x 1.55m, z −3.0m, 0.45–2.15m up).
    printshop: { screen: { pos: [0.705, 0.591, -1.33], size: [0.47, 0.81, 0.08] } },
  },
  // HYPE-U, THE HYPETUNES UNIVERSITY (2026-11-01). Khari: "I want to add a new
  // venue called Hype-U. it will be our learning university. The content from
  // the learn page will be the content, and it will be taught by the same
  // artists, using their voice and text content. Inside, build a central lobby
  // with chairs and a bookshelf … On one wall, place a sign directing users to
  // take an elevator to get to their class. When they step into the elevator,
  // the floor buttons are labeled after the course title. When the elevator
  // makes it to the floor, open the door to a small classroom with the
  // artist/instructor standing in front of a whiteboard."
  //   THE LOBBY (scripts/make_hypeu.mjs; room space = its printout,
  //   scripts/out/hypeu_marks.json): the elevator centred on the back wall, the
  //   sign beside it, the bookshelf on the west wall (`mark_bookshelf`: where
  //   you stand to browse — the books are for later), six chairs.
  //   THE ELEVATOR (components/hypeu): step into the car and the floors are the
  //   courses (GET /api/learn); a floor is the classroom below, one room id per
  //   course (city/hypeu ensureClassroom).
  hypeu: {
    name: 'Hype-U',
    accent: '#d8a640',
    // `?v=` — BUMP IT WHENEVER THE GLB IS REBUILT. Models are served
    // `immutable` for a year under assetUrl's one global version, so a rebuilt
    // room under the same URL never reaches anyone who loaded the old one
    // (2026-11-01: the classroom's new 2m board was live, and Khari's browser
    // kept drawing the old 1.2m one around the new page).
    model: '/models/venues/hypeu.glb?v=20261101b',
    // Just inside the glass doors, looking down the aisle at the elevator.
    spawn: { pos: [0, 0.75, 2], look: [0, 0.705, -1.727] },
    firstPerson: true,
    look: { tiltMin: -0.5, tiltMax: 0.6 },
    orbit: { min: 0.4, max: 30, pan: true },
    exit: { pos: [0, 0, 2.591], size: [0.982, 1.6, 0.24] },
    light: 1.05,
    // Standing in the city (the 'university' type is sited), the glass doors show
    // the real street, so the daylight painted behind them comes down.
    sitedHide: ['daylight'],
    // Chairs are offered as you walk up to them (components/NearSeats), and held
    // while you sit (shared, like the café's).
    sharedSeats: true,
    seats: ['chair_a', 'chair_b', 'chair_c', 'chair_d', 'chair_e', 'chair_f'],
    // A university is quiet (2026-11-01, Khari: "mute the radio when a user
    // enters Hype-U"): the outside radio is held off in the lobby and every
    // classroom, back on the way out (RoomSound). A room playlist or ambience
    // later is the server's (city_rooms → RoomSoundtrack), keyed by room id.
    ownSound: true,
    hypeu: 'lobby',
    elevator: {
      car: { min: [-0.477, 0, -2.727], max: [0.477, 0, -1.727] },
      door: { pos: [0, 0, -1.75], w: 0.636, h: 1.091, rotY: 0 },
      arrive: { pos: [0, 0.75, -2.182], look: [0, 0.682, 0.091] },
    },
  },
  // A HYPE-U CLASSROOM (scripts/make_hypeu_class.mjs): the template every
  // course's floor is registered from (city/hypeu ensureClassroom adds
  // `course`). Never entered as itself. No street door — the elevator is the way
  // out. The instructor (the course's host) stands at `teacher`, the board
  // carries the class and its lesson, page by page (components/hypeu
  // Classroom); sit, and the lesson starts (components/hypeu CourseRunner).
  // `seatKeepAlive`: the class keeps your chair while it runs or you are asking
  // (API sharedSeatStore keepActive) — two quiet minutes stand you up.
  hypeu_class: {
    name: 'Hype-U classroom',
    accent: '#d8a640',
    model: '/models/venues/hypeu_class.glb?v=20261101c',   // bump on every rebuild (see hypeu above)
    // You arrive in the elevator, facing the board.
    spawn: { pos: [0, 0.75, 1.5], look: [0, 0.636, -2.045] },
    firstPerson: true,
    look: { tiltMin: -0.5, tiltMax: 0.6 },
    orbit: { min: 0.4, max: 30, pan: true },
    light: 1.05,
    sharedSeats: true,
    seatKeepAlive: true,
    seats: ['chair_1', 'chair_2', 'chair_3', 'chair_4', 'chair_5', 'chair_6'],
    ownSound: true,   // the radio stays off in class (see hypeu)
    hypeu: 'class',
    elevator: {
      car: { min: [-0.477, 0, 1.045], max: [0.477, 0, 2.045] },
      door: { pos: [0, 0, 1.068], w: 0.636, h: 1.091, rotY: Math.PI },
      arrive: { pos: [0, 0.75, 1.5], look: [0, 0.636, -2.045] },
    },
    // The lesson is written on the board (3.6 x 2m, left of centre); the
    // instructor stands just past its right end, out of its way.
    whiteboard: { pos: [-0.159, 0.773, -2.011], size: [1.636, 0.909], rotY: 0 },
    teacher: { pos: [0.909, 0, -1.75], rotY: -0.493 },
  },
  subway: {
    name: 'The Underground',
    accent: '#8fb7ff',
    model: '/models/venues/subway_station.glb',
    // Rebuilt 2026-07-28 from DOCS/WORLD/old_metro_tunnel_free.glb, the ORIGINAL
    // download — the shipped metro.glb had been joined, which merges primitives
    // by material and leaves no object names to remove anything by.
    //
    //   --strip=^node_0   THE WHOLE PROPS LAYER. This model splits cleanly in
    //     two: everything structural is named for the primitive it was built
    //     from (Plane = shell, floor, ceiling and beams; Cube.001 = the central
    //     divider; Cylinder.002 = the track bed), and every piece of clutter is
    //     `node_0.*`. The clutter was 91% OF THE TRIANGLES — 153,480 -> 12,884,
    //     10.7MB -> 1.7MB, 11 draw calls — for trash piles standing in the ring
    //     at the centre, which is not somewhere the player goes.
    //
    //     The worst single offender was node_0.014_Material.013_0: 49,980
    //     triangles inside a 1.2m box, a third of the model on its own.
    //
    //     TO BRING SOMETHING BACK (benches went with the trash — they are in
    //     here somewhere), narrow the regex and rebuild:
    //       node scripts/extract_model.mjs DOCS/WORLD/old_metro_tunnel_free.glb \
    //         - public/models/venues/metro.glb 1024 0 "--strip=^node_0\.(0|1)"
    //     Find the piece you want with scripts/mesh_bounds.mjs, which prints
    //     each object's size and position next to its name. The source download
    //     is untouched, so nothing here is a one-way door.
    //
    // ── SCALE ────────────────────────────────────────────────────────────────
    // Measured 21.8 x 2.84 x 27.7 in METRES. One world unit is ~2.2m here, so
    // the 27.7m long axis is 12.6 world units — the same conversion the corner
    // market uses. Without this it rendered at 1:1 and the station was 27.7
    // units long, two and a bit times its real size, which is what made it feel
    // like walking through a canyon.
    // 102.99m on the long axis ÷ 2.2 = 46.8 units. MeshInterior fits by
    // `fitTo / max(size.x, size.z)`, so this is the long axis and nothing else.
    //
    // Nearly FOUR TIMES the station it replaces (the old one was 27.7m / 12.6u),
    // which is the single biggest thing to have in mind when reading the numbers
    // below — every one of them was re-derived, none survived the swap.
    fitTo: 46.8,
    // ── AN ISLAND PLATFORM ───────────────────────────────────────────────────
    // This is the shape that decides where you may stand. There are track beds
    // on BOTH sides — model x −9.35..−6.45 and 6.35..9.25, sitting 1.4m below
    // the platform — with the walkable platform between them and the train
    // standing on the +x track. So the room is a long central island with a drop
    // either side, not a corridor with walls.
    //
    // ── READ OFF THE PLAN, NOT DERIVED FROM THE BOUNDS ───────────────────────
    // The first version of this put x at 0 because that is the middle of the
    // model, and the middle of the model is a row of BENCHES AND COLUMNS —
    // scripts/xz_map.mjs at walker height (0.6..1.8) shows geometry at model
    // x −0.7..+0.75 down the whole length, and the train filling x +5.0..+10.5.
    // Open platform is what is left: roughly x −6.4..−0.7 on the west side.
    //
    // So this stands at model x −3.5 (÷2.2 = −1.59), mid-way along the west
    // platform with the benches to your right and the train beyond them.
    //
    // STILL PROVISIONAL. Fly with F (Q/E), stand where it should be, and run
    // world.where() — it prints this line ready to paste. That is the only way
    // this gets right; deriving it from a bounding box is what put the last one
    // inside the furniture.
    // Stood at the bottom of the stairs and read off ⚙ → cam. The stairs are the
    // arrival point for everything: you come through the street portal and are
    // simply THERE, and the exit portal to the next city sits in the same place.
    // They are never climbed, so the station stays one floor.
    spawn: { pos: [-0.11, 1.13, -13.94], look: [-0.07, 1.28, -11.95] },
    // 46.8 units end to end — by far the longest interior in the world, and the
    // one that exposed interiors walking at 2.4 u/s against the street's 9. At
    // 6 it takes ~17s to pace the platform, which is a walk rather than a
    // commute. Tune live with ?walkspeed= and bake the number back here.
    walkSpeed: 6,
    // NO `walk` BLOCK, deliberately, and it is not an omission.
    //
    // MeshInterior calls setRoomBounds(null) for every mesh room — the floor is
    // the boundary, answered by FloorClamp's raycast, which is exact for a shape
    // no box can describe. A `walk` box here is read by nobody: it is dead
    // config that LOOKS live, and the camera panel correctly reports "bounds:
    // none" while it sits in the file, which is how somebody loses an afternoon
    // tuning numbers that do nothing.
    //
    // Splat rooms still need one. They have no geometry to raycast against.
    firstPerson: true,
    look: { tiltMin: -0.3, tiltMax: 0.5 },
    orbit: { min: 0.4, max: 40, pan: true },
  },
  salon: {
    name: 'The Salon',
    accent: '#f0a6c0',
    // The room has its own sound — gossip on the floor, the news on the TV — so
    // the district station fades out on the way in and back on the way out
    // (RoomSound + audioEngine.roomQuiet). Only ever RESTORED, never imposed:
    // walk in with the music already off and it stays off when you leave.
    ownSound: true,
    model: '/models/venues/salon.glb',
    // ── THE FIRST ROOM WE BUILT RATHER THAN FOUND ───────────────────────────
    //
    //   node scripts/make_salon.mjs     ← regenerates the GLB in ~1 second
    //
    // Source of truth is scripts/make_salon.mjs, not the .glb. To move a chair,
    // widen the room or add a fifth station, edit the descriptor at the top of
    // that file and re-run it. The binary is build output.
    //
    // NOTE WHAT IS MISSING FROM THIS ENTRY, because it is the entire argument
    // for authoring:
    //
    //   no fitTo      — authored in metres and converted at 1u ≈ 2.2m on the way
    //                   out (scripts/lib/kit.mjs). The metro needed 12.6 and the
    //                   market 13.4, each derived by measuring props inside the
    //                   model and reasoning back to a real-world size, and each
    //                   invalidating that room's spawn when it changed.
    //   no yNudge     — the floor's top surface is at y=0 BY CONSTRUCTION, so
    //                   detectFloor has nothing to infer.
    //   no --strip    — nothing is in here that nobody put here.
    //   no modelRotY  — it was authored facing the way it faces.
    //
    // 58,944 triangles, 2.1MB, 61 draw calls, NO TEXTURES — the whole look is
    // geometry and a 23-colour palette. 611 loose parts (bulbs, bottles, towels,
    // mouldings) merge by material at build time; the ~49 objects carrying
    // affordances are kept whole, which is the one thing merging must not eat.
    // For comparison the subway entrance arrived at 125MB.
    //
    // WHAT MAKES IT READ AS BOUGHT rather than blocked out, since the first pass
    // of this room was four chairs and two sinks in a box:
    //   architecture   dropped soffit over the styling run, arch on pilasters
    //                  framing the wash alcove, coffered ceiling, cornice,
    //                  skirting, panelled wainscot, inlaid floor border
    //   edges          every manufactured object is a rounded box, a lathe or an
    //                  extruded profile, so a specular line runs down each edge.
    //                  A 90° edge returns one flat value and reads as a blockout
    //                  no matter how well proportioned it is.
    //   dressing       bottles, jars, towels, brushes, hairdryers, a trolley, a
    //                  broom, magazines, plants, a coat stand. A salon with
    //                  nothing left out is a showroom of salon furniture.
    //
    // AFFORDANCES COME FROM THE BUILDER. Seats, surfaces, mirrors, shelves and
    // the two postable walls carry `userData` (written as glTF node extras, read
    // back by GLTFLoader) naming what they are and which way they face — because
    // the code that placed them knew, and a mesh alone never can. Read them off
    // any child: `child.userData.affordance`.
    //   seat      12 of them — styling chairs, wash chairs, hood dryers, stools,
    //             the sofa — each with `facing` and `seatHeight`
    //   surface   counter tops, colour bar, reception, trolley, side table
    //   mirror    the four station mirrors
    //   shelf     retail, four levels
    //   lamp      soffit cove, coffer downlights
    //   postable  accent wall, retail panel, the sign over reception
    //   album_slot  12 RECORD SLEEVES racked in the retail case, at and above
    //             eye height. Each is its own mesh — the merge pass keeps
    //             affordance-bearing meshes whole precisely so there is a real
    //             surface here to swap artwork onto. Authored at 314mm square,
    //             the real sleeve size, so cover art lands at the right
    //             proportion with nothing to tune. `facing` and `size` are on
    //             each one; which shelves rack albums is `retail.albums` in the
    //             generator, so a fifth shelf of them is a one-line change.
    //
    // Lighting: pale walls and a lot of white stone bounce plenty, so the rig
    // runs under 1. Ceiling is at 1.55u, hence the practicals just below it.
    light: 0.9,
    lampHeight: 1.4,
    // RESIDENTS — `slug` joins to models/avatars/roster.json.
    //
    // Unlike the splat rooms, none of these were authored by eye: the room came
    // out of a descriptor, so the furniture coordinates are KNOWN and every
    // position below is derived from it, divided by 2.2 on the way out because
    // the descriptor is in metres and the world is not.
    //
    // Sassy La starts BEHIND THE RECEPTION DESK — x 3.6m (SALON.reception.x), in
    // the ~1.1m gap between the desk's back edge (z 3.42m) and the back bar
    // against the +z wall (z 4.58m), turned to face into the room. That is the
    // staff side: the desk's curved front bulges toward −z, into the customer's
    // half. It also puts her in shot on arrival, since the spawn looks diagonally
    // across her.
    //
    // Overridden the moment anything is saved for this room in world_room_objects
    // (⚙ → Interior avatar), which is what to nudge with rather than this line.
    residents: [
      { slug: 'sassy_la', pos: [1.64, 0, 1.82], rotY: 3.14, height: 0.75 },
    ],
    // STAGE MARKS — the places a character can be SENT, by name.
    //
    // The room owns these and the scene owns the cue, so a scene says "go to the
    // chair" and never learns a coordinate. Move the wash alcove in the
    // descriptor and this is the one line that follows it; the scene does not
    // change at all.
    //
    // `chair` is a metre to the +x side of wash chair 1 (x −6.2m, z −3.65m, i.e.
    // SALON.basins[0] and cz = −d/2 + 1.35), turned to face it. Beside the chair,
    // not on top of it — she is working on someone in it.
    //
    // `home` is omitted deliberately: home is wherever the resident was PLACED,
    // so a position nudged live in ⚙ → Interior avatar is still the place she
    // walks back to. Duplicating it here would mean she returns to a coordinate
    // that has not been true since the last time anyone tuned the room.
    stage: {
      sassy_la: {
        // x −2.36 → −2.13: half a metre FURTHER from the chair (Khari,
        // 2026-08-03 — on his double-wide monitor the wider frame crops
        // tighter vertically and her model clipped the camera at 1m; ~1.5m
        // keeps her in frame at every aspect). Serving distance, not spawn:
        // spawn is the resident placement above (⚙-tunable); THIS mark is
        // file-authored only.
        chair: { pos: [-2.13, 0, -1.66], rotY: -1.57 },
      },
    },
    // AND THE CUE. Sitting down in a salon is a request for service, so the
    // stylist comes over — after a beat, because arriving the instant you land
    // reads as a trigger firing rather than as someone noticing you.
    //
    // Deliberately not in `stage`: the marks are geography and outlive any
    // particular scene, while this is a beat somebody wrote and is the thing
    // most likely to be replaced by the dialogue director naming its own cues.
    // One cue per room for now — enough for the salon, and honest about it.
    onSit: { slug: 'sassy_la', mark: 'chair', delayMs: 3200 },
    // Just inside the entrance (x +2.73, z +2.27), turned to take the room in
    // diagonally: the styling run and its mirrors down the left wall, the arch
    // and wash alcove beyond them, reception forward-left, waiting on your right.
    spawn: { pos: [2.73, 0.75, 1.85], look: [-0.5, 0.68, -0.5] },
    firstPerson: true,
    // WALK OUT TO LEAVE (2026-10-31): the front door in the south wall
    // (make_salon: x 6.0m, 1.8m wide, inner face z 5m = 2.27u) — the spawn
    // stands just inside it.
    exit: { pos: [2.727, 0, 2.3], size: [0.82, 1.6, 0.24] },
    look: { tiltMin: -0.3, tiltMax: 0.5 },
    orbit: { min: 0.4, max: 40, pan: true },
    // No `walk` block: FloorClamp raycasts down onto the floor and the walkable
    // area is the floor's real outline. It has no opinion about walls, so you
    // can still step up onto a counter — the same limitation every mesh room
    // has, and the reason this room's furniture is authored at real heights.
  },
  grocery: {
    name: 'Corner Market',
    accent: '#ffd27d',
    // Tuned in-world. Multiplies MeshInterior's whole rig — hemisphere fill,
    // ambient and ceiling practicals together — so the balance holds and this
    // only moves exposure. Below 1 because the shop's own white fixtures and
    // pale floor already bounce a lot.
    light: 0.79,
    // SITED: a public place belongs to the city. The shop stands at its venue
    // anchor, at that building's coordinates, and its windows look out on the
    // real street — the city keeps rendering while you are inside, culled to
    // SITED_CULL (70u) so you get the facing block and nothing behind it.
    //
    // The private rooms stay virtual: an apartment is instanced at the origin
    // with the world hidden, because there is no one place in the city it "is".
    placement: 'sited',
    // WHICH BUILDING IT STANDS ON. Uncomment with a mesh name to pin it there;
    // leave it off and the sampler scatters this venue with the others, which is
    // fine for a first look and wrong the moment you care about the view out.
    //
    // Find the name in-world: ?pick=1, then shift-click the building — the HUD
    // reads back what you hit. A NAME rather than coordinates because the city
    // is auto-fitted, so every coordinate in it moves if the model is re-exported
    // or the fit changes, while the name survives.
    // anchorMesh: 'LM_Liquor',
    // Inert while `placement` is 'sited' (PlanetScene: showBackdrop = !sited &&
    // …). Kept so dropping the line above falls back to the sealed-with-a-
    // horizon look rather than to black, which is what the windows show with
    // neither set.
    exterior: 'backdrop',
    // Which way the shopfront faces once it is standing in the city. The model's
    // own orientation has no reason to match the street it was anchored to, and
    // geometry cannot say which wall is the front. Tune live with ?venuerot=
    // (degrees) and bake the number here.
    modelRotY: 0,
    // Just under the ceiling, which fitTo now puts at 2.0 world units (the
    // model's real 4.42m). Was 5.2, tuned when the model measured 33.6 tall
    // because of the floating stock — that hung the practicals two and a half
    // times higher than the roof they are supposed to be mounted in.
    lampHeight: 1.8,
    //timeOfDay: 'midday',
    model: '/models/venues/supermarket.glb',
    // THE OUTSIDE, standing in the city all the time. Extracted from the same
    // source as the interior (--shell), so the two line up by construction
    // rather than by tuning. Without it a sited venue is an invisible hole in
    // the street and VenueShells falls back to a massing block.
    shell: '/models/venues/supermarket_shell.glb',
    // ── SHELL ALIGNMENT — set these by eye, they are meant to be ────────────
    //
    // The shell auto-fits and auto-grounds exactly as the interior does, and it
    // is usually right. When it is not, these two win outright rather than being
    // one more input to an inference. Two numbers you can see the effect of beat
    // a heuristic that is correct most of the time and unexplainable when it is
    // not — the shell and the interior are separate models fitted by separate
    // code, and closing the last few centimetres between them by hand is faster
    // and more honest than another rule.
    //
    //   shellFit   the shell's longest horizontal span, in world units. Leave
    //              unset to inherit the interior's measured footprint. Set it if
    //              the shell reads bigger or smaller than the room inside it.
    //   shellY     lifts (+) or sinks (−) the shell only, in world units, after
    //              grounding. The venue tool's Y moves BOTH; this is the trim
    //              between them.
    //
    // The console prints `[shell] floor -> …` and `[venue] grocery: …` on entry;
    // read them together and the gap is the number to type here.
    // shellFit: 13.4,
    // shellY: 0,
    // Rebuilt 2026-07-27 from DOCS/WORLD/models/corner_store, which is the
    // ORIGINAL download — 3,483 separate meshes, where our previous build had
    // been joined to 27 and had lost the object structure that both of these
    // passes need:
    //
    //   --keep-y=:8   140 product meshes were floating at y 22-29, sixteen units
    //                 above the roof, with nothing in between. They contributed
    //                 6k triangles and, far worse, made the model measure 33.6
    //                 TALL — so every automatic fit here sized a room, a light
    //                 rig and a camera against a building six times its real
    //                 height. That is what made the shop feel doll-sized.
    //                 Now 29.5 x 10.2 x 18.1, which is a supermarket.
    //   --thin=0.3    2,325 duplicate products removed across 36 groups, at
    //                 least one of every product kept. 388k -> 210k triangles.
    //                 Simplification was the wrong lever: a 20-triangle cereal
    //                 box cannot lose half its triangles and still be a box.
    //
    // Aim a re-clip with scripts/y_profile.mjs, which prints where the geometry
    // actually sits — a bounding box cannot tell a basement from a roof.
    //
    // ── SCALE, DERIVED RATHER THAN EYEBALLED ────────────────────────────────
    //
    // THE SET SCALES, NOT THE PLAYER. Your eye height is a world constant —
    // shared by the street, the residents, multiplayer peers, walk speed and the
    // camera's near plane — so changing it per room means being a different size
    // when you walk out and peers rendering wrong beside you. A downloaded
    // model's scale, by contrast, is simply WRONG until corrected, and wrong the
    // same way in every zone and story instance that ever uses it. Correct it
    // once, here, and nothing downstream has to compensate.
    //
    // The number is measured, not guessed. This model is authored in REAL
    // METRES — confirmed by three independent references in it: the `свет`
    // ceiling fixtures sit at 4.42, the `белый` shelving bays are 2.2 tall, and
    // the `стекло холод` fridges are 2.2. Those are correct supermarket
    // dimensions, so the model's unit IS a metre.
    //
    // The world's unit is not. A 0.75 eye height standing in for a ~1.65m human
    // eye makes ONE WORLD UNIT ≈ 2.2 METRES, so a metric model converts at
    // 1/2.2 = 0.4545. Across the 29.5m shop floor that is 13.4 world units.
    //
    // Checks out both ways: shelving lands at 1.0 units against a 0.75 eye
    // (1.33x, exactly 2.2m against 1.65m), and the ceiling at 2.0 (2.7x, exactly
    // 4.42m against 1.65m). You stand in it at the height you stand in a shop.
    //
    // NOT fitted to the bounding box, deliberately — that box includes roof
    // structure above the ceiling you actually walk under, which is what made
    // every earlier attempt oversize the room around the player.
    fitTo: 13.4,
    // Eye height 0.75 — the WORLD's human scale (PlanetScene's EYE), not the
    // 1.6 these rooms were authored with. 1.6 is a person in metres, and this
    // world is not in metres: it made you 2.1x taller indoors than you were on
    // the pavement a second earlier, which is why a shop tuned to look right
    // still felt built for a child.
    //
    // z 3, not 8. The old value was authored against the model's raw 18.1m
    // depth; at fitTo the shop is 8.2 units deep, so z 8 stood you outside the
    // back wall. Room-local spawns are in WORLD units and do not scale with
    // fitTo, so this has to move whenever the fit does.
    spawn: { pos: [0, 0.75, 3], look: [0, 0.7, -3] },
    // WALK OUT THE DOOR TO LEAVE (2026-10-31, Khari: "the user cannot walk out of
    // a room … corner market"). ⚠ THE STOREFRONT IS −z, not +z: measured off the
    // shell (raycast at eye height along the walls), the +z wall is solid and the
    // glass front with its pillars is −z, the sliding doors at its west end
    // (room x −5.8..−3.9, glass at z −3.3), the floor running under them to
    // z −3.77. The box spans the doorway from just inside the glass to the
    // floor's edge, at the floor's height (1.26, below). You come out a step
    // past it on the pavement (Portals outsideOf).
    exit: { pos: [-4.85, 1.26, -3.5], size: [1.8, 1.6, 0.4] },
    // STORY MARKS (City Stories inside scenes, 2026-10-29): where an artist stands
    // when their episode has a scene in here. Room-local, WORLD units like spawn,
    // feet on the floor; rotY 0 faces +z (toward the spawn; the door is −z, see
    // `exit`). `in0` = the
    // lead, `in1` = a co-star, turned a little toward each other. A first
    // placement in front of the spawn: tune in-world.
    //
    // ⚠ THE FLOOR IN HERE IS AT y 1.26, NOT 0 (measured 2026-10-30, below). y was
    // 0, which stood a story's artists 1.26u under the floor. Fixed with the
    // cashier's marks; x/z untouched.
    storyMarks: {
      in0: { pos: [-0.5, 1.26, 1.1], rotY: 0.4 },
      in1: { pos: [0.5, 1.26, 1.1], rotY: -0.4 },
    },
    // ── THE REGISTER (2026-10-30) ────────────────────────────────────────────
    // Khari: "another type of job: grocer/cashier. We have a corner market with
    // a job for an artist to work the cash register." The job is API
    // `grocery.cashier` (migration 453, GroceryService ring_up): cast it in ⚙ →
    // Interior avatar → job "Cashier", on the body behind the counter.
    //
    // MEASURED, NOT EYEBALLED: the GLB loaded with three's GLTFLoader and fitted
    // the way MeshInterior does (measureBounds, detectFloor, fitTo 13.4 → x0.4547,
    // centre x 0.671 / z 0.059 m). ⚠ detectFloor finds NO floor in this model
    // (merged junk reaches y −2.68m, so the real floor isn't in the bottom 20%),
    // so room y 0 is the box bottom and the real floor (`Plane.001`, y 0.088m)
    // is at room y 1.26. The body already placed back there (object 24) stands
    // at y 1.24, which agrees. The checkout: counter `Cube.3625` (x −11.55..−10.29,
    // z 3.51..5.94 m, top 0.919 m, an L with its foot at the north end), the
    // till `Cube.3626` on the counter's east edge (z 4.31..4.95 m), its screen
    // facing west, knee space under the west edge: the cashier stands WEST of
    // the counter facing the shop floor (+x); customers come up on the east.
    //
    //   register  behind the counter at the till (model −11.40, 4.78 m), facing
    //             +x. A counter job is walked here when it hands something over
    //             (CounterItems), and home is wherever the body was placed.
    //   counter   the counter top just south of the till (model −10.62, 0.919,
    //             5.30 m), flat for 0.3u along it: where what was rung up is set
    //             down, in a paper bag (CounterItems). Room space, like `pickup`.
    stage: {
      '*': {
        register: { pos: [-5.488, 1.26, 2.146], rotY: 1.571 },
      },
    },
    counter: [-5.134, 1.638, 2.383],
    // SOLID (2026-10-31, state/interiorSolids): a downloaded model has no
    // furniture tags, so the checkout is a box: the L above in room space
    // (model x −11.55..−10.29, z 3.51..5.94 m, floor 0.088 → top 0.919 m, through
    // the fit). Not the mesh `Cube.3625` itself — it also carries a low part
    // running 6.7m to z −0.77, which would wall off the aisle. The box takes in
    // the L's inside corner, which is the cashier's side anyway.
    solid: { boxes: [{ name: 'checkout', x: [-5.557, -4.984], z: [1.569, 2.674], y: [1.26, 1.638] }] },
    firstPerson: true,
    look: { tiltMin: -0.3, tiltMax: 0.5 },
    orbit: { min: 0.4, max: 40, pan: true },
  },
  jahva: {
    name: 'The Jahva House',
    accent: '#e0a15e',
    // ── A LOFT COFFEE SHOP, BUILT FOR THE PART (2026-10-30) ─────────────────
    // Khari: "It is a loft style coffee shop with a barista and tables you can
    // sit at. near the back (upstairs preferably) is a small listening area
    // where spoken word performances will happen. This is another 'plaza' type
    // venue, but quiet, over coffee and words."
    //
    //   node scripts/make_jahva.mjs     ← regenerates the GLB in ~1 second, and
    //                                     PRINTS the room-space numbers below
    //
    // Source of truth is the generator, not the .glb (46k triangles, 1.6MB,
    // 84 draw calls; three small textures — brick, oak, the night street —
    // drawn by the script and baked in, because a runtime texture set is keyed
    // on room id and this room will stand in the city under the town's name).
    // Not in TOWN_LOCATIONS: the venue registry places it (venueTypes
    // 'coffee-shop'), and an instance is `{ ...this, its own name }`.
    //
    // 10 x 16 x 6m (4.55 x 7.27 x 2.73u). Door in the south wall (+z), steel
    // windows down the west wall (−x), the bar along the east wall (+x) with
    // exposed brick behind it, a mezzanine across the back third (−z) at 2.9m
    // (1.318u) reached by an open stair against the east wall, and the
    // spoken-word corner up there in the back-left: a 200mm platform, stool,
    // mic, a light cone from a can overhead, a couch and two armchairs.
    //
    // ROOM SPACE = AUTHORED SPACE. The generator measures the merged scene the
    // way MeshInterior does and prints the centre (0, 0) and the floor (0): the
    // view out of the windows lives inside the walls' thickness so nothing
    // pokes past the box the room is centred on. Every number below is pasted
    // from that print, metres ÷ 2.2, and is true with no fitTo.
    model: '/models/venues/jahva.glb',
    // Standing in the city (the 'coffee-shop' type is sited), the windows show
    // the real street, so the night street painted behind the glass comes down.
    sitedHide: ['jahva_night'],
    // TWO STOREYS. InteriorWalk stops pinning your eye to the arrival height
    // and FloorClamp's probe sets it from the floor under you — so the stair
    // (16 risers of 0.082u against a 0.35u step) carries you up, and a drop of
    // more than 0.8u is refused as a ledge, which is what holds you at the
    // mezzanine edge (the railing is drawn, not solid). No other room changes.
    multiLevel: true,
    // "quiet, over coffee and words": the district station fades out on the
    // way in and back on the way out (RoomSound), so a reading upstairs is not
    // competing with the street's radio. Restored, never imposed.
    ownSound: true,
    // Warm and a little dim — brick and a dark ceiling drink light, and the
    // pendants (emissive shades) carry the warmth. lampHeight 2.0 keeps the
    // practical above the mezzanine deck AND the key spot (lamp × 1.25 = 2.5u)
    // under the 2.73u ceiling: above it, the slab would shadow the whole room.
    light: 0.85,
    lampHeight: 2.0,
    // THE TABLES — t1..t3 down the windows (front to back), t4..t6 in the
    // middle column. Each has two chairs, `chair_<t>_a` on its north side and
    // `chair_<t>_b` on its south, both facing it — `seats` are the chair UNIT
    // names (what world.mark / sitAt target; the seat mesh inside each is
    // `<unit>_seat`). `cups` are the table top (0.78m) 17cm in front of each
    // seat, in seat order; the same points are empty nodes `cup_<t>_a|b` in the
    // GLB. `serve` stands 0.75m east of the table on the aisle side, clear of
    // both chairs, facing it — `face: '<table>'`, a target, not an angle
    // (city/facing faceMarks).
    tables: [
      { id: 't1', seats: ['chair_t1_a', 'chair_t1_b'], cups: [[-1.591, 0.355, 2.195], [-1.591, 0.355, 2.35]], serve: { pos: [-1.25, 0, 2.273], face: 't1' } },
      { id: 't2', seats: ['chair_t2_a', 'chair_t2_b'], cups: [[-1.591, 0.355, 0.968], [-1.591, 0.355, 1.123]], serve: { pos: [-1.25, 0, 1.045], face: 't2' } },
      { id: 't3', seats: ['chair_t3_a', 'chair_t3_b'], cups: [[-1.591, 0.355, -0.259], [-1.591, 0.355, -0.105]], serve: { pos: [-1.25, 0, -0.182], face: 't3' } },
      { id: 't4', seats: ['chair_t4_a', 'chair_t4_b'], cups: [[-0.273, 0.355, 2.195], [-0.273, 0.355, 2.35]], serve: { pos: [0.068, 0, 2.273], face: 't4' } },
      { id: 't5', seats: ['chair_t5_a', 'chair_t5_b'], cups: [[-0.273, 0.355, 0.968], [-0.273, 0.355, 1.123]], serve: { pos: [0.068, 0, 1.045], face: 't5' } },
      { id: 't6', seats: ['chair_t6_a', 'chair_t6_b'], cups: [[-0.273, 0.355, -0.259], [-0.273, 0.355, -0.105]], serve: { pos: [0.068, 0, -0.182], face: 't6' } },
    ],
    // THE PICKUP — the brass pad at the bar's north end (`pickup_top`, surface
    // 'pickup'), at counter height (1.05m), kept empty so a cup can be set
    // down on it. Node `pickup_spot` in the GLB.
    pickup: [1.295, 0.477, 0.932],
    // The listening corner's seats: tagged `seat` (zone 'spoken_word') but not
    // in `tables` — they are upstairs, and a resident walks to a mark in a
    // straight line, so table service up there would float across the room.
    spokenSeats: ['listen_couch', 'listen_chair_a', 'listen_chair_b'],
    // SHARED, LIMITED SEATS (2026-10-30). Khari: "This indoor room is a shared
    // live experience (like the plaza), so seats will actually be limited.
    // Prevent sitting at one if it is taken, and limit the time a user can sit
    // (2 min)." Every chair above is asked for from the server (API
    // sockets/sharedSeatStore) before you sit, held two minutes, and shown as
    // taken to everyone else in the room. Instances inherit it (`...base`).
    sharedSeats: true,
    // WALK OUT THE DOOR TO LEAVE (2026-10-31, as HQ): the street door in the
    // south wall (make_jahva: x −1.4m, 1.5m wide, inner face z 8m = 3.64u), from
    // just inside it to the floor's edge — clear of the spawn (z 3.05).
    exit: { pos: [-0.636, 0, 3.66], size: [0.68, 1.6, 0.24] },
    // HEADPHONES ON EVERY TABLE (2026-10-31). Khari: "let's make the spoken word
    // tracks available on a playlist at each table. Place a pair of headphones
    // on the table (not blocking the coffee). If the user selects them, they see
    // a list of recordings that play just for them." TableHeadphones lays a pair
    // on each table's window side (clear of the cups and the sugar/candle/book);
    // HeadphonesDock plays the pieces (API GET /api/world/spoken-word).
    headphones: true,
    // STAGE MARKS. `'*'` = whoever is cast in the job (any body).
    //   bar       behind the counter at the espresso machine, facing the room
    //   gate      the barista aisle's north mouth, then `gate_out` the customer
    //   gate_out  side of the counter's end: the way OUT from behind the bar. A
    //             straight walk from `bar` to a table passes through the
    //             counter; bar → gate → gate_out → serve goes round it.
    //   spoken    on the platform upstairs, behind the mic, facing the couch
    //             (y 1.409 = deck 1.318 + the 200mm platform). Empty nodes
    //             `mark_<name>` in the GLB carry the same points.
    stage: {
      '*': {
        bar: { pos: [1.773, 0, 1.636], rotY: -1.571 },
        gate: { pos: [1.773, 0, 0.523], rotY: 3.142 },
        gate_out: { pos: [1.045, 0, 0.523], rotY: -1.571 },
      },
      spoken: { pos: [-1.364, 1.409, -3.273], face: [-1.364, -2.27] },   // the couch's middle (listen_couch)
    },
    // STORY MARKS — ground floor, in the aisle between the middle tables and
    // the bar (clear of every serve mark by ~0.67u), turned toward the door
    // and a little toward each other. rotY 0 faces +z, as everywhere.
    storyMarks: {
      in0: { pos: [0.341, 0, 1.659], rotY: -0.306 },
      in1: { pos: [0.75, 0, 1.659], rotY: -1.106 },
    },
    // Just inside the door (x −1.4m, z +6.7m), looking up the room toward the
    // bar's pickup end: tables on the left, the bar on the right, the stair
    // and the mezzanine's railing ahead.
    spawn: { pos: [-0.636, 0.75, 3.045], look: [1.091, 0.72, 0.727] },
    // TONIGHT'S SETS on an A-frame board (VenueSetList). It was under the stairs
    // (x 4.3m, z −1.5m, facing west) — "too tucked away" (Khari, 2026-11-01, a
    // screenshot standing where it should go): now out on the open floor beside
    // the stair's top end, in front of the coffee sacks (x ≈3.5m, z ≈−3.4m,
    // measured off that screenshot from the old board), turned toward the door so
    // it is seen on the way in. Fine-tune by standing there: ⚙ → Placeables →
    // "Set list board" (a placed board wins over this).
    setList: { pos: [1.6, 0, -1.54], rotY: -0.4535 },
    firstPerson: true,
    // Wider than the salon's: from the mezzanine you look DOWN into the café
    // (2u below, ~3u away is −0.6), and up at it from the tables.
    look: { tiltMin: -0.6, tiltMax: 0.6 },
    orbit: { min: 0.4, max: 40, pan: true },
  },
};

// Marks that say what they face (`face`) get their rotY from it, once, here.
for (const [id, room] of Object.entries(ROOMS)) faceMarks(room, id);

// ── The town block ────────────────────────────────────────────────────────
// The four locations are spread across a deep block (not a row): two face the
// main street, two sit back — one down an alley, the concert hall at the far
// corner. Each has a `world` footprint (where its building stands, facades on
// −z), a `street` camera pose (where you "arrive" when you pick it on the map),
// and a `map` coord (0..1, for the HUD minimap; x = left→right, y = far→near).
// Camera approaches from −z, so a smaller z is nearer the viewer.
export const STREET_OVERVIEW = { pos: [0, 8, -14], look: [1, 3, 18] };

// ⚠ NOT ONLY CONSTANTS ANY MORE (2026-10-30). The venues the town votes for
// arrive at runtime (GET /api/world/venues) and are written into ROOMS and this
// array IN PLACE by city/venueRegistry — entries flagged `registry: true`, built
// from a type in venueTypes.js. Read both at render, never copy them at module
// load, or a venue that goes live mid-session will be missing from your copy.

export const TOWN_LOCATIONS = [
  {
    key: 'studio', name: 'The Studio', accent: '#ffb25e', interiorId: 'studio',
    world: [-22, 12], map: [0.16, 0.74],
    street: { pos: [-22, 3.2, 3], look: [-22, 2.9, 12] },
  },
  {
    key: 'listening-room', name: 'Listening Room', accent: '#ff5e78', interiorId: 'listening-room',
    world: [-6, 27], map: [0.42, 0.30], // down the left alley, set deep
    street: { pos: [-6, 2.8, 15], look: [-6, 2.6, 27] },
  },
  {
    key: 'record-store', name: 'Record Store', accent: '#9dff7d', interiorId: 'record-store',
    world: [20, 12], map: [0.78, 0.74],
    street: { pos: [20, 3.2, 3], look: [20, 2.9, 12] },
  },
  {
    key: 'concert-hall', name: 'Concert Hall', accent: '#7dd3ff', interiorId: 'concert-hall',
    world: [22, 31], map: [0.84, 0.22], // far corner, biggest venue
    street: { pos: [22, 4.5, 19], look: [22, 3.6, 31] },
  },
  {
    key: 'theater', name: 'The Cinema', accent: '#c8a2ff', interiorId: 'theater',
    world: [4, 40], map: [0.52, 0.08], // deepest back-center of the block
    street: { pos: [4, 3.2, 28], look: [4, 2.9, 40] },
  },
  // The two mesh venues. Placed on the OPPOSITE side of the main street from
  // the music venues, so the block reads as a neighbourhood with everyday
  // places in it rather than a row of five studios. These world coords are a
  // first pass — the real placement is whichever buildings you anchor them to.
  {
    key: 'grocery', name: 'Corner Market', accent: '#ffd27d', interiorId: 'grocery',
    world: [-38, 24], map: [0.06, 0.36],
    street: { pos: [-38, 3.2, 14], look: [-38, 2.9, 24] },
  },
  {
    key: 'salon', name: 'The Salon', accent: '#f0a6c0', interiorId: 'salon',
    world: [-30, 6], map: [0.16, 0.68], // everyday side, between market and street
    street: { pos: [-30, 3.2, -4], look: [-30, 2.9, 6] },
  },
  {
    // NOT A BUILDING ANY MORE (2026-10-31, Khari: "remove 'The Underground' from
    // its current building. I have placed a subway entrance that leads to the
    // underground"): no venue anchor (CityModel VENUE_ORDER), so no building is
    // dressed as it and no marker stands on one; the way in is the entrance
    // placed with the portal tool (world_objects #83, 41.29 / −31.11, turned
    // −1.54: it opens west), and leaving lands you in front of it (Portals →
    // worldStore.streetDoors). These coords follow it, for the map and the tool.
    key: 'subway', name: 'The Underground', accent: '#8fb7ff', interiorId: 'subway',
    world: [41.3, -31.1], map: [0.60, 0.92],
    street: { pos: [38.3, 3.2, -31.1], look: [41.3, 2.9, -31.1] },
  },
  {
    // The small club (see ROOMS.warehouse). PROVISIONAL world coords — Khari
    // placed the actual door with the portal tool; this entry is what puts the
    // room in that tool's target list, on the map, and gives it a marker. Night
    // side, near the Underground: a club by the subway entrance reads right.
    key: 'warehouse', name: 'The Warehouse', accent: '#7dffd0', interiorId: 'warehouse',
    world: [-14, -4], map: [0.28, 0.85],
    street: { pos: [-14, 3.2, -14], look: [-14, 2.9, -4] },
  },
];

export const streetPoseFor = (key) => TOWN_LOCATIONS.find((l) => l.key === key)?.street || null;

// ── YOU GO IN THROUGH THE DOOR ───────────────────────────────────────────────
// A venue is entered through a PLACED PORTAL — a trigger volume somebody put in
// front of the entrance with the admin tool — not by clicking the building.
//
// The building-as-hit-zone came first because it always works: no placement, no
// tuning, click the massing block and you are inside. What it costs is the
// premise. A building you click is a menu item wearing a facade, it has no
// front, and it cannot be guarded — the doorman is standing at an entrance that
// is one of a thousand equally valid ways in, which is exactly the problem the
// concert hall's guard exposed.
//
// WHAT THIS DOES NOT TOUCH: the venue markers and the map. Both are wayfinding
// UI, presented as UI, and both still enter. The thing being retired is the
// pretence that a BUILDING is a button — clickable through a wall, on the roof,
// or from across the block, which is what leaves a venue with no front and
// nothing that can be stationed at it.
//
// So a venue with no portal is not stranded; it is only unreachable by walking
// up to it, which is the mode the whole change is about. Portals still warns by
// name about every venue missing a door, because "you cannot walk in" is not
// obvious from a building that silently ignores you.
//
// Every route in — door, marker, map, fast travel — goes through the same
// access gate in enterInterior, so none of this is a way past a guard.
//
// ?hitzone=1 restores the old clicks for a session, for placing the first door
// in a world that has none.
export const ENTRY_VIA_PORTAL = typeof window === 'undefined'
  || new URLSearchParams(window.location.search).get('hitzone') == null;
